import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import {
    ArrowUp, ArrowDown, Trash, PencilSimple, Plus,
    ArrowCounterClockwise, X, CheckCircle,
    FloppyDisk, DotsSixVertical,
} from "@phosphor-icons/react";

/**
 * IntakeTemplateSettings — V1.5.6.2 admin page at /settings/screening-intake.
 * Admin/superadmin only. Manage the WORKSPACE-wide default intake form
 * fields: reorder, edit, add, remove, toggle-required, restore defaults.
 *
 * Data model: `workspace_intake_templates` — one doc per workspace with an
 * ordered array of `IntakeField` objects. Effective form on any candidate
 * = this list + per-job extras from `jobs.intake_extra_fields`.
 */
const FIELD_TYPES = [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "select", label: "Select (dropdown)" },
    { value: "boolean", label: "Yes / No" },
    { value: "date", label: "Date" },
    { value: "duration_ym", label: "Duration (Years + Months)" },
];

export default function IntakeTemplateSettings() {
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [editingKey, setEditingKey] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/settings/intake-template");
            setFields(r.data.fields || []);
            setDirty(false);
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const move = (idx, dir) => {
        const t = idx + dir;
        if (t < 0 || t >= fields.length) return;
        const next = [...fields];
        [next[idx], next[t]] = [next[t], next[idx]];
        setFields(next);
        setDirty(true);
    };

    const toggleRequired = async (f) => {
        try {
            const r = await api.patch(`/settings/intake-template/fields/${f.key}`, {
                required: !f.required,
            });
            setFields((prev) => prev.map((x) => (x.key === f.key ? r.data : x)));
            toast.success(`${f.label} is now ${r.data.required ? "required" : "optional"}`);
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        }
    };

    const removeField = async (f) => {
        if (!window.confirm(`Remove "${f.label}" from every intake form in this workspace?`)) return;
        try {
            await api.delete(`/settings/intake-template/fields/${f.key}`);
            setFields((prev) => prev.filter((x) => x.key !== f.key));
            toast.success(`Removed · ${f.label}`);
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        }
    };

    const saveField = async (patch) => {
        const key = editingKey;
        try {
            const r = await api.patch(`/settings/intake-template/fields/${key}`, patch);
            setFields((prev) => prev.map((x) => (x.key === key ? r.data : x)));
            setEditingKey(null);
            toast.success("Field updated");
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        }
    };

    const addField = async (payload) => {
        try {
            const r = await api.post("/settings/intake-template/fields", payload);
            setFields((prev) => [...prev, r.data]);
            setShowAdd(false);
            toast.success(`Added · ${r.data.label}`);
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        }
    };

    const persistReorder = async () => {
        setSaving(true);
        try {
            await api.put("/settings/intake-template", { fields });
            setDirty(false);
            toast.success("Order saved");
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const restoreDefaults = async () => {
        if (!window.confirm("Restore the shipped 10 default intake fields? Any customisations will be lost.")) return;
        setSaving(true);
        try {
            const r = await api.post("/settings/intake-template/restore-defaults");
            setFields(r.data.fields || []);
            setDirty(false);
            toast.success("Restored NaukriDaddy defaults");
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const requiredCount = useMemo(() => fields.filter((f) => f.required).length, [fields]);

    if (loading) {
        return (
            <div className="px-4 sm:px-8 py-10 text-[#525252]"
                 data-testid="intake-settings-loading">
                Loading intake template…
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto"
             data-testid="intake-settings-page">
            <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Admin only · workspace preferences
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Screening intake fields.
                </h1>
                <p className="text-sm text-[#525252] mt-2 max-w-xl">
                    These fields appear on the Intake tab of every candidate in this workspace
                    and (unless optional) block advancement to Interview / Offer / Hired until filled.
                    Reorder, tweak labels, or add new fields specific to your business.
                </p>
            </div>

            {err && (
                <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-3 py-2 mb-4"
                     data-testid="intake-settings-error">
                    {err}
                </div>
            )}

            {/* Summary strip */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-[#E5E5E5]">
                <div className="flex items-center gap-3 sm:gap-4 text-xs text-[#525252]">
                    <span data-testid="intake-settings-total">
                        <span className="font-black text-ink">{fields.length}</span> field{fields.length === 1 ? "" : "s"}
                    </span>
                    <span className="text-[#A3A3A3]">·</span>
                    <span data-testid="intake-settings-required-count">
                        <span className="font-black text-amber-700">{requiredCount}</span> required
                    </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {dirty && (
                        <button
                            onClick={persistReorder}
                            disabled={saving}
                            data-testid="intake-settings-save-order"
                            className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                        >
                            <FloppyDisk size={11} weight="fill" /> Save order
                        </button>
                    )}
                    <button
                        onClick={restoreDefaults}
                        disabled={saving}
                        data-testid="intake-settings-restore"
                        className="border border-[#E5E5E5] hover:border-ink text-[#525252] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                    >
                        <ArrowCounterClockwise size={11} weight="bold" />
                        <span>Restore defaults</span>
                    </button>
                </div>
            </div>

            {/* Field list */}
            <div className="space-y-2" data-testid="intake-settings-list">
                {fields.map((f, idx) => (
                    <FieldRow
                        key={f.key}
                        f={f}
                        idx={idx}
                        total={fields.length}
                        allFields={fields}
                        isEditing={editingKey === f.key}
                        onEdit={() => setEditingKey(f.key)}
                        onCancelEdit={() => setEditingKey(null)}
                        onSaveEdit={saveField}
                        onMoveUp={() => move(idx, -1)}
                        onMoveDown={() => move(idx, 1)}
                        onToggleRequired={() => toggleRequired(f)}
                        onRemove={() => removeField(f)}
                    />
                ))}
            </div>

            {/* Add field */}
            <div className="mt-4">
                {!showAdd ? (
                    <button
                        onClick={() => setShowAdd(true)}
                        data-testid="intake-settings-add-btn"
                        className="w-full border border-dashed border-[#A3A3A3] hover:border-cobalt text-[#525252] hover:text-cobalt py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors"
                    >
                        <Plus size={12} weight="bold" /> Add intake field
                    </button>
                ) : (
                    <AddFieldForm
                        onCancel={() => setShowAdd(false)}
                        onAdd={addField}
                    />
                )}
            </div>

            <p className="text-[10px] text-[#A3A3A3] mt-6">
                Tip: recruiters can add ONE-OFF fields per job from the candidate's Intake tab.
                Add here only what you want on every candidate across the whole workspace.
            </p>
        </div>
    );
}

function FieldRow({
    f, idx, total, isEditing, onEdit, onCancelEdit, onSaveEdit,
    onMoveUp, onMoveDown, onToggleRequired, onRemove, allFields,
}) {
    if (isEditing) {
        return (
            <EditFieldRow
                f={f}
                allFields={allFields}
                onCancel={onCancelEdit}
                onSave={onSaveEdit}
            />
        );
    }
    return (
        <div
            data-testid={`intake-field-row-${f.key}`}
            className="bg-white border border-[#E5E5E5] hover:border-ink px-3 py-3 transition-colors"
        >
            {/* Row-1: reorder handles + label + type/required meta. Wraps freely
                on mobile so nothing gets truncated. */}
            <div className="flex items-start gap-2 sm:gap-3">
                <div className="flex flex-col gap-0.5 shrink-0 pt-0.5">
                    <button
                        onClick={onMoveUp}
                        disabled={idx === 0}
                        aria-label="Move up"
                        data-testid={`intake-field-up-${f.key}`}
                        className="text-[#A3A3A3] hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                    >
                        <ArrowUp size={12} weight="bold" />
                    </button>
                    <button
                        onClick={onMoveDown}
                        disabled={idx === total - 1}
                        aria-label="Move down"
                        data-testid={`intake-field-down-${f.key}`}
                        className="text-[#A3A3A3] hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                    >
                        <ArrowDown size={12} weight="bold" />
                    </button>
                </div>
                <DotsSixVertical size={14} weight="bold"
                                 className="text-[#E5E5E5] shrink-0 mt-1 hidden sm:block" />
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <p className="text-sm font-bold text-ink break-words">
                            {f.label}
                        </p>
                        {f.unit && (
                            <span className="text-[10px] text-[#A3A3A3] shrink-0">({f.unit})</span>
                        )}
                        {f.builtin ? (
                            <span className="text-[9px] font-bold uppercase text-[#A3A3A3] bg-[#F5F5F5] px-1.5 py-0.5 shrink-0">
                                Default
                            </span>
                        ) : (
                            <span className="text-[9px] font-bold uppercase text-cobalt bg-cobalt/10 px-1.5 py-0.5 shrink-0">
                                Custom
                            </span>
                        )}
                    </div>
                    <p className="text-[10px] text-[#A3A3A3] mt-0.5 leading-relaxed">
                        <span className="font-bold">{FIELD_TYPES.find((t) => t.value === f.type)?.label || f.type}</span>
                        {f.type === "select" && f.options?.length ? ` · ${f.options.length} options` : ""}
                        {" · "}
                        <span className={f.required ? "text-amber-700 font-bold" : ""}>
                            {f.required ? "Required" : "Optional"}
                        </span>
                        {f.show_if?.key && (
                            <>
                                {" · "}
                                <span className="text-cobalt font-bold">
                                    Shows if {f.show_if.key} = {String(f.show_if.equals)}
                                </span>
                            </>
                        )}
                        {(f.validators || []).length > 0 && (
                            <>
                                {" · "}
                                <span className="text-[#525252] font-bold">
                                    {f.validators.map((v) => v.kind).join(", ")}
                                </span>
                            </>
                        )}
                    </p>
                </div>
                {/* Desktop-only action cluster — stays inline on ≥sm screens. */}
                <div className="hidden sm:flex items-center gap-1 shrink-0">
                    <button
                        onClick={onToggleRequired}
                        data-testid={`intake-field-toggle-required-${f.key}`}
                        title={f.required ? "Make optional" : "Make required"}
                        className={`px-2 py-1 text-[9px] font-bold uppercase tracking-wider border transition-colors ${
                            f.required
                                ? "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
                                : "bg-white border-[#E5E5E5] text-[#525252] hover:border-ink"
                        }`}
                    >
                        {f.required ? "Required" : "Optional"}
                    </button>
                    <button
                        onClick={onEdit}
                        data-testid={`intake-field-edit-${f.key}`}
                        aria-label="Edit field"
                        className="p-1.5 text-[#525252] hover:text-cobalt hover:bg-cobalt/5 transition-colors"
                    >
                        <PencilSimple size={12} weight="bold" />
                    </button>
                    <button
                        onClick={onRemove}
                        data-testid={`intake-field-remove-${f.key}`}
                        aria-label="Remove field"
                        className="p-1.5 text-[#525252] hover:text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
                    >
                        <Trash size={12} weight="bold" />
                    </button>
                </div>
            </div>
            {/* Row-2: mobile-only action cluster — full-width row so labels
                above never truncate. Hidden on ≥sm. */}
            <div className="flex sm:hidden items-center gap-2 mt-3 pt-3 border-t border-[#F5F5F5]">
                <button
                    onClick={onToggleRequired}
                    data-testid={`intake-field-toggle-required-${f.key}-mobile`}
                    className={`flex-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                        f.required
                            ? "bg-amber-50 border-amber-200 text-amber-800"
                            : "bg-white border-[#E5E5E5] text-[#525252]"
                    }`}
                >
                    {f.required ? "Required" : "Optional"}
                </button>
                <button
                    onClick={onEdit}
                    data-testid={`intake-field-edit-${f.key}-mobile`}
                    aria-label="Edit"
                    className="inline-flex items-center justify-center gap-1 px-3 py-1.5 border border-[#E5E5E5] text-[#525252] text-[10px] font-bold uppercase tracking-wider"
                >
                    <PencilSimple size={11} weight="bold" /> Edit
                </button>
                <button
                    onClick={onRemove}
                    aria-label="Remove"
                    data-testid={`intake-field-remove-${f.key}-mobile`}
                    className="inline-flex items-center justify-center px-3 py-1.5 border border-[#E5E5E5] text-[#DC2626]"
                >
                    <Trash size={11} weight="bold" />
                </button>
            </div>
        </div>
    );
}

function EditFieldRow({ f, onCancel, onSave, allFields }) {
    const [label, setLabel] = useState(f.label || "");
    const [unit, setUnit] = useState(f.unit || "");
    const [hint, setHint] = useState(f.hint || "");
    const [options, setOptions] = useState((f.options || []).join(", "));
    const [showIfKey, setShowIfKey] = useState(f.show_if?.key || "");
    const [showIfVal, setShowIfVal] = useState(
        f.show_if?.equals === true ? "true"
        : f.show_if?.equals === false ? "false"
        : (f.show_if?.equals ?? "")
    );
    const [validators, setValidators] = useState(f.validators || []);

    const depField = (allFields || []).find((x) => x.key === showIfKey);

    const toggleValidator = (kind, extra = {}) => {
        setValidators((prev) => {
            const idx = prev.findIndex((v) => v.kind === kind);
            if (idx >= 0) return prev.filter((_, i) => i !== idx);
            return [...prev, { kind, ...extra }];
        });
    };
    const updateValidator = (kind, patch) => {
        setValidators((prev) => prev.map((v) => (v.kind === kind ? { ...v, ...patch } : v)));
    };
    const hasValidator = (kind) => validators.some((v) => v.kind === kind);
    const getValidator = (kind) => validators.find((v) => v.kind === kind);

    const submit = () => {
        const payload = { label: label.trim() };
        if (unit.trim() !== (f.unit || "")) payload.unit = unit.trim() || null;
        if (hint.trim() !== (f.hint || "")) payload.hint = hint.trim() || null;
        if (f.type === "select") {
            const opts = options.split(",").map((s) => s.trim()).filter(Boolean);
            if (!opts.length) {
                toast.error("Select fields need at least one option");
                return;
            }
            payload.options = opts;
        }
        // Show-if
        if (showIfKey) {
            const dep = (allFields || []).find((x) => x.key === showIfKey);
            let equals = showIfVal;
            if (dep?.type === "boolean") equals = showIfVal === "true";
            else if (dep?.type === "number") equals = showIfVal === "" ? null : Number(showIfVal);
            payload.show_if = { key: showIfKey, equals };
        } else {
            payload.show_if = null;
        }
        payload.validators = validators;
        onSave(payload);
    };
    return (
        <div
            data-testid={`intake-field-edit-row-${f.key}`}
            className="bg-white border-2 border-cobalt p-3 space-y-2"
        >
            <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cobalt">
                    Edit · {f.key}
                </p>
                <button onClick={onCancel} className="p-0.5 text-[#A3A3A3] hover:text-ink">
                    <X size={12} weight="bold" />
                </button>
            </div>
            <input
                data-testid="intake-edit-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Field label"
                className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                    data-testid="intake-edit-unit"
                    type="text"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder="Unit (LPA, days, yrs…)"
                    className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt"
                />
                <input
                    data-testid="intake-edit-hint"
                    type="text"
                    value={hint}
                    onChange={(e) => setHint(e.target.value)}
                    placeholder="Helper text (optional)"
                    className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt"
                />
            </div>
            {f.type === "select" && (
                <input
                    data-testid="intake-edit-options"
                    type="text"
                    value={options}
                    onChange={(e) => setOptions(e.target.value)}
                    placeholder="Options, comma-separated"
                    className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                />
            )}

            {/* Conditional visibility */}
            <div className="border-t border-[#F5F5F5] pt-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1.5">
                    Show only if
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <select
                        data-testid="intake-edit-showif-key"
                        value={showIfKey}
                        onChange={(e) => { setShowIfKey(e.target.value); setShowIfVal(""); }}
                        className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt"
                    >
                        <option value="">Always show (no condition)</option>
                        {(allFields || [])
                            .filter((x) => x.key !== f.key)
                            .map((x) => (
                                <option key={x.key} value={x.key}>{x.label}</option>
                            ))}
                    </select>
                    {depField?.type === "boolean" ? (
                        <select
                            data-testid="intake-edit-showif-value"
                            value={showIfVal}
                            onChange={(e) => setShowIfVal(e.target.value)}
                            disabled={!showIfKey}
                            className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt disabled:opacity-40"
                        >
                            <option value="">— equals —</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                    ) : depField?.type === "select" ? (
                        <select
                            data-testid="intake-edit-showif-value"
                            value={showIfVal}
                            onChange={(e) => setShowIfVal(e.target.value)}
                            disabled={!showIfKey}
                            className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt disabled:opacity-40"
                        >
                            <option value="">— equals —</option>
                            {(depField.options || []).map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            data-testid="intake-edit-showif-value"
                            type="text"
                            value={showIfVal}
                            disabled={!showIfKey}
                            onChange={(e) => setShowIfVal(e.target.value)}
                            placeholder="equals value"
                            className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt disabled:opacity-40"
                        />
                    )}
                </div>
            </div>

            {/* Validators */}
            <div className="border-t border-[#F5F5F5] pt-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-1.5">
                    Validation
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {["email", "phone_in", "url"].map((kind) => (
                        <button
                            key={kind}
                            type="button"
                            data-testid={`intake-edit-validator-${kind}`}
                            onClick={() => toggleValidator(kind)}
                            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                                hasValidator(kind)
                                    ? "bg-cobalt border-cobalt text-white"
                                    : "bg-white border-[#E5E5E5] text-[#525252] hover:border-ink"
                            }`}
                        >
                            {kind === "phone_in" ? "Phone (IN)" : kind === "email" ? "Email" : "URL"}
                        </button>
                    ))}
                    {f.type === "number" && (
                        <>
                            <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                                Min
                                <input
                                    data-testid="intake-edit-validator-min"
                                    type="number"
                                    value={getValidator("min")?.value ?? ""}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === "") { toggleValidator("min"); return; }
                                        if (!hasValidator("min")) toggleValidator("min", { value: Number(v) });
                                        else updateValidator("min", { value: Number(v) });
                                    }}
                                    className="w-16 bg-white border border-[#E5E5E5] px-2 py-0.5 text-xs focus:outline-none focus:border-cobalt"
                                />
                            </label>
                            <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                                Max
                                <input
                                    data-testid="intake-edit-validator-max"
                                    type="number"
                                    value={getValidator("max")?.value ?? ""}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === "") { toggleValidator("max"); return; }
                                        if (!hasValidator("max")) toggleValidator("max", { value: Number(v) });
                                        else updateValidator("max", { value: Number(v) });
                                    }}
                                    className="w-16 bg-white border border-[#E5E5E5] px-2 py-0.5 text-xs focus:outline-none focus:border-cobalt"
                                />
                            </label>
                        </>
                    )}
                    <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                        Regex
                        <input
                            data-testid="intake-edit-validator-regex"
                            type="text"
                            value={getValidator("regex")?.pattern ?? ""}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === "") { if (hasValidator("regex")) toggleValidator("regex"); return; }
                                if (!hasValidator("regex")) toggleValidator("regex", { pattern: v });
                                else updateValidator("regex", { pattern: v });
                            }}
                            placeholder="^[A-Z]{2}\d+$"
                            className="w-36 bg-white border border-[#E5E5E5] px-2 py-0.5 text-xs font-mono focus:outline-none focus:border-cobalt"
                        />
                    </label>
                </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
                <button
                    onClick={onCancel}
                    data-testid="intake-edit-cancel"
                    className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-ink px-2 py-1"
                >
                    Cancel
                </button>
                <button
                    onClick={submit}
                    data-testid="intake-edit-save"
                    className="bg-cobalt hover:bg-cobalt-dark text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1.5 inline-flex items-center gap-1"
                >
                    <CheckCircle size={10} weight="fill" /> Save
                </button>
            </div>
        </div>
    );
}

function AddFieldForm({ onCancel, onAdd }) {
    const [label, setLabel] = useState("");
    const [type, setType] = useState("text");
    const [unit, setUnit] = useState("");
    const [required, setRequired] = useState(false);
    const [optionsRaw, setOptionsRaw] = useState("");
    const submit = () => {
        if (!label.trim()) {
            toast.error("Label is required");
            return;
        }
        const payload = {
            label: label.trim(),
            type,
            required,
            unit: unit.trim() || undefined,
        };
        if (type === "select") {
            payload.options = optionsRaw.split(",").map((s) => s.trim()).filter(Boolean);
            if (!payload.options.length) {
                toast.error("Select fields need at least one option (comma-separated)");
                return;
            }
        }
        onAdd(payload);
    };
    return (
        <div
            data-testid="intake-settings-add-form"
            className="border-2 border-cobalt bg-[#F9FBFF] p-4 space-y-3"
        >
            <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cobalt">
                    Add new intake field
                </p>
                <button onClick={onCancel} className="p-0.5 text-[#A3A3A3] hover:text-ink">
                    <X size={12} weight="bold" />
                </button>
            </div>
            <input
                data-testid="intake-settings-new-label"
                type="text"
                placeholder="Label — e.g. Preferred office"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select
                    data-testid="intake-settings-new-type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt"
                >
                    {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                </select>
                <input
                    data-testid="intake-settings-new-unit"
                    type="text"
                    placeholder="Unit (LPA, days…)"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt"
                />
                <label className="inline-flex items-center gap-2 text-xs text-[#525252] cursor-pointer px-2 bg-white border border-[#E5E5E5]">
                    <input
                        data-testid="intake-settings-new-required"
                        type="checkbox"
                        checked={required}
                        onChange={(e) => setRequired(e.target.checked)}
                        className="accent-cobalt"
                    />
                    Required
                </label>
            </div>
            {type === "select" && (
                <input
                    data-testid="intake-settings-new-options"
                    type="text"
                    placeholder="Options, comma-separated"
                    value={optionsRaw}
                    onChange={(e) => setOptionsRaw(e.target.value)}
                    className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                />
            )}
            <div className="flex items-center justify-end gap-2">
                <button
                    onClick={onCancel}
                    data-testid="intake-settings-new-cancel"
                    className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-ink px-2 py-1"
                >
                    Cancel
                </button>
                <button
                    onClick={submit}
                    data-testid="intake-settings-new-submit"
                    className="bg-cobalt hover:bg-cobalt-dark text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1.5 inline-flex items-center gap-1"
                >
                    <Plus size={10} weight="bold" /> Add field
                </button>
            </div>
        </div>
    );
}
